import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, ExternalLink, XCircle } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import {
  getDirectorInviteByToken,
  linkDirectorInvite,
  respondToDirectorInvite,
} from "../../services/kybService";

const getApiMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const DirectorInvitePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const requestIdFromLink = searchParams.get("requestId") || "";
  const { isAuthenticated } = useAuth();
  const [invite, setInvite] = useState(null);
  const [isLoading, setIsLoading] = useState(Boolean(token));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const effectiveRequestId = invite?.requestId;
  const mobileDeepLink = useMemo(() => {
    const params = new URLSearchParams();
    if (requestIdFromLink) {
      params.set("requestId", requestIdFromLink);
    }
    if (token) {
      params.set("token", token);
    }
    return `com.80Eight://business/director-invite?${params.toString()}`;
  }, [requestIdFromLink, token]);

  useEffect(() => {
    let isActive = true;

    async function loadInvite() {
      if (!token) {
        setErrorMessage("This director invite link is missing its token.");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await getDirectorInviteByToken(token);
        if (isActive) {
          setInvite(response?.data || response);
          setErrorMessage("");
        }
      } catch (error) {
        if (isActive) {
          setErrorMessage(
            getApiMessage(error, "We could not load this director invite.")
          );
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    }

    loadInvite();

    return () => {
      isActive = false;
    };
  }, [token]);

  const handleDecision = async (action) => {
    if (!token || !effectiveRequestId) {
      setErrorMessage(
        "We could not resolve this invite. Please reopen the link."
      );
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage("");
      setSuccessMessage("");
      await linkDirectorInvite(token);
      await respondToDirectorInvite({ requestId: effectiveRequestId, action });
      setSuccessMessage(
        action === "approve"
          ? "Director invite approved."
          : "Director invite declined."
      );
      setTimeout(
        () => navigate("/trading/digitalWallet?tab=business-account"),
        900
      );
    } catch (error) {
      setErrorMessage(
        getApiMessage(error, "We could not record your director response.")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogin = () => {
    const returnTo = `${window.location.pathname}${window.location.search}`;
    navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`);
  };

  return (
    <main className="min-h-screen bg-slate-950 px-4 py-8 text-white">
      <section className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-2xl items-center">
        <div className="w-full rounded-lg border border-white/10 bg-white p-6 text-slate-950 shadow-2xl md:p-8">
          <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-[#57249c]">
            Director invite
          </p>
          <h1 className="mb-3 text-2xl font-semibold">
            Business approval request
          </h1>
          <p className="mb-6 text-sm text-slate-600">
            Review the request using the same email address this invite was sent
            to.
          </p>

          {isLoading && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm">
              Loading invite details...
            </div>
          )}

          {!isLoading && invite && (
            <div className="mb-6 rounded-md border border-slate-200">
              <div className="grid grid-cols-1 gap-0 text-sm md:grid-cols-2">
                <div className="border-b border-slate-200 p-4 md:border-r">
                  <div className="text-xs uppercase text-slate-500">
                    Business
                  </div>
                  <div className="mt-1 font-semibold">
                    {invite.businessName}
                  </div>
                </div>
                <div className="border-b border-slate-200 p-4">
                  <div className="text-xs uppercase text-slate-500">
                    Reference
                  </div>
                  <div className="mt-1 font-semibold">
                    {invite.requestReference}
                  </div>
                </div>
                <div className="border-b border-slate-200 p-4 md:border-b-0 md:border-r">
                  <div className="text-xs uppercase text-slate-500">
                    Director
                  </div>
                  <div className="mt-1 font-semibold">
                    {invite.directorInvite?.fullName ||
                      invite.directorInvite?.email}
                  </div>
                </div>
                <div className="p-4">
                  <div className="text-xs uppercase text-slate-500">Status</div>
                  <div className="mt-1 font-semibold">
                    {invite.directorInvite?.status || invite.requestStatus}
                  </div>
                </div>
              </div>
            </div>
          )}

          {errorMessage && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {errorMessage}
            </div>
          )}

          {successMessage && (
            <div className="mb-4 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700">
              {successMessage}
            </div>
          )}

          {!isAuthenticated ? (
            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                className="rounded-md bg-[#57249c] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#432076]"
                onClick={handleLogin}
                type="button"
              >
                Login to respond
              </button>
              <a
                className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-800 transition hover:bg-slate-50"
                href={mobileDeepLink}
              >
                Open app <ExternalLink size={16} />
              </a>
            </div>
          ) : (
            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                className="inline-flex items-center justify-center gap-2 rounded-md bg-[#22c6ab] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#1da089] disabled:cursor-not-allowed disabled:opacity-60"
                disabled={isSubmitting || isLoading || !invite}
                onClick={() => handleDecision("approve")}
                type="button"
              >
                <CheckCircle size={17} /> Approve
              </button>
              <button
                className="inline-flex items-center justify-center gap-2 rounded-md border border-red-200 px-4 py-3 text-sm font-semibold text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={isSubmitting || isLoading || !invite}
                onClick={() => handleDecision("decline")}
                type="button"
              >
                <XCircle size={17} /> Decline
              </button>
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default DirectorInvitePage;
